import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Search, X, Plus, CheckSquare, Square, ArrowLeft, Save, AlertTriangle } from 'lucide-react';

const COMPETITOR_OPTIONS = ['Invisalign', 'Ormco Spark', 'Dentcare Aligners', 'Smile Aligners', 'Illusion Aligners'];

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CALL_TYPES = ['In-Person Visit', 'Telephonic Call'];
const CALL_DURATION_OPTIONS = ['1–5 mins', '5–15 mins', '15+ mins'];
const WHO_MET_OPTIONS = ['Met Decision Maker', 'Met Non-Decision Maker'];
const IS_DM = {
  'Met Decision Maker': true, 'Met Non-Decision Maker': false,
  'Connected with Decision Maker': true, 'Connected with Non-Decision Maker': false,
};
const TELEPHONIC_PURPOSES = [
  'Cold call to promote a product',
  'Call based on a doctor referral to promote a product',
  'Follow up call post a Webinar / Education session',
  'Complaint Addressal Call',
  'Others',
];
const INPERSON_CATEGORIES = [
  'Product Promotion Call', 'Complaint Addressal Call',
  'Workshop Invite Call', 'Payment Follow-Up Call',
  'Case Follow-Up Call', 'Others',
];
const DESIGNATIONS = ['Clinic Owner (Orthodontist)', 'Clinic Owner (Non-Orthodontist)', 'Consultant (Orthodontist)', 'Consultant (Non-Orthodontist)', 'Others'];

const COMPLAINT_FOLLOWUP_ACTIONS = [
  'Replace product',
  'Share technical document',
  'Connect doctor to Orthodontist',
  'Connect with global technician and get back',
  'Connect with vendor and get back',
  'Advised corrections and doctor agreed',
];

const TELEPHONIC_EXTRA_ACTION = 'Schedule a call for the representative to meet in person';

const FOLLOW_UP_ACTIONS = [
  'Share pricing / online resources with doctor',
  'Schedule a follow-up call',
  'Arrange a call with Synapse Orthodontist',
  'Share call details with Synapse Manager for inputs',
];
const WORKSHOP_OUTCOMES = ['Interested', 'Not Interested', 'Neutral – Need Follow-Up'];
const PAYMENT_OUTCOMES = ['Positive', 'Negative', 'Neutral – Need Follow-Up'];
const FOLLOW_UP_OPTIONS = ['In 7 Days', 'In 14 Days', 'Custom Date'];
const TELEPHONIC_FOLLOW_UP_OPTIONS = ['In 7 Days', 'In 14 Days', 'Custom Date', 'Representative to take over the account'];
const DESIGNATION_OPTIONS = [
  'Clinic Owner (Orthodontist)', 'Clinic Owning (Non-Orthodontist)',
  'Consultant Orthodontist', 'Others',
];
// Fields required from 2nd call onwards
const SECOND_CALL_FIELDS = ['clinic_potential'];

const getMissingFields = (account, isFirstCall = false) => {
  const missing = [];
  if (!account.name) missing.push('Account Name');
  if (!account.address) missing.push('Address');
  if (!account.city) missing.push('City');
  if (!isFirstCall && !account.clinic_potential) missing.push('Clinic Potential');
  // Contacts not required — rep can add inline during call entry
  return missing;
};
// ── Helper Components ─────────────────────────────────────────────────────────
const Chip = ({ label, selected, onClick, color = 'violet', fullWidth = false }) => {
  const colors = {
    violet: selected ? 'bg-teal-700 text-white border-violet-600 shadow-md' : 'bg-white text-slate-600 border-slate-200 hover:border-violet-400',
    emerald: selected ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-slate-600 border-slate-200 hover:border-emerald-400',
    amber: selected ? 'bg-amber-500 text-white border-amber-500' : 'bg-white text-slate-600 border-slate-200 hover:border-amber-400',
    red: selected ? 'bg-red-500 text-white border-red-500' : 'bg-white text-slate-600 border-slate-200 hover:border-red-400',
  };
  return (
    <button type="button" onClick={onClick}
      className={`${fullWidth ? 'w-full' : ''} px-4 py-3 rounded-xl border-2 text-sm font-semibold transition-all active:scale-95 ${colors[color]}`}>
      {label}
    </button>
  );
};

const Section = ({ step, title, subtitle, children }) => (
  <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
    <div className="flex items-start gap-3 mb-4">
      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-teal-700 to-emerald-500 flex items-center justify-center flex-shrink-0 mt-0.5">
        <span className="text-white text-xs font-bold">{step}</span>
      </div>
      <div>
        <h2 className="text-base font-bold text-slate-800">{title}</h2>
        {subtitle && <p className="text-xs text-slate-400 mt-0.5">{subtitle}</p>}
      </div>
    </div>
    {children}
  </div>
);

const Checklist = ({ options, selected, onChange, othersText, onOthersChange }) => {
  const toggle = opt => onChange(selected.includes(opt) ? selected.filter(x => x !== opt) : [...selected, opt]);
  return (
    <div className="space-y-2">
      {options.map(opt => (
        <button key={opt} type="button" onClick={() => toggle(opt)}
          className="flex items-center gap-3 w-full text-left px-3 py-3 rounded-xl border border-slate-200 hover:border-teal-300 active:bg-teal-50 transition-all">
          {selected.includes(opt) ? <CheckSquare className="h-5 w-5 text-teal-700 flex-shrink-0" /> : <Square className="h-5 w-5 text-slate-300 flex-shrink-0" />}
          <span className="text-sm text-slate-700">{opt}</span>
        </button>
      ))}
      <button type="button" onClick={() => toggle('Others')}
        className="flex items-center gap-3 w-full text-left px-3 py-3 rounded-xl border border-slate-200 hover:border-teal-300 active:bg-teal-50 transition-all">
        {selected.includes('Others') ? <CheckSquare className="h-5 w-5 text-teal-700 flex-shrink-0" /> : <Square className="h-5 w-5 text-slate-300 flex-shrink-0" />}
        <span className="text-sm text-slate-700">Others</span>
      </button>
      {selected.includes('Others') && (
        <Input value={othersText} onChange={e => onOthersChange(e.target.value)}
          placeholder="Please specify..." className="rounded-xl" />
      )}
    </div>
  );
};

// ── Main Component ────────────────────────────────────────────────────────────
const CreateSalesCall = ({ onLogout }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const searchRef = useRef(null);

  const [callDate, setCallDate] = useState(new Date().toISOString().slice(0, 10));
  const [callType] = useState('In-Person Visit'); // Always field call
  const [callDuration, setCallDuration] = useState('');
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [accountSearch, setAccountSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [searching, setSearching] = useState(false);
  const [missingFields, setMissingFields] = useState([]);
  const [inlinePotential, setInlinePotential] = useState('');
  const [inlineCity, setInlineCity] = useState('');
  const [showNewAccountForm, setShowNewAccountForm] = useState(false);
  const [newAccountData, setNewAccountData] = useState({ name: '', address: '', city: '', pincode: '', clinic_potential: '' });
  const [savingNewAccount, setSavingNewAccount] = useState(false);
  const [duplicateSuggestions, setDuplicateSuggestions] = useState([]);
  const [checkingDuplicateAccount, setCheckingDuplicateAccount] = useState(false);
  const [whoMet, setWhoMet] = useState('');
  const [contactPersonId, setContactPersonId] = useState('');
  const [callCategory, setCallCategory] = useState('');
  const [telephonicPurpose, setTelephonicPurpose] = useState('');
  const [telephonicOthers, setTelephonicOthers] = useState('');
  const [othersNotes, setOthersNotes] = useState('');
  const [workshopDetails, setWorkshopDetails] = useState('');
  const [workshopOutcome, setWorkshopOutcome] = useState('');
  const [paymentOutcome, setPaymentOutcome] = useState('');
  // Complaint specific
  const [complaintProducts, setComplaintProducts] = useState([]);
  const [complaintProductsOthers, setComplaintProductsOthers] = useState('');
  const [complaintClosed, setComplaintClosed] = useState('');
  const [complaintFollowUps, setComplaintFollowUps] = useState([]);
  const [complaintFollowUpsOthers, setComplaintFollowUpsOthers] = useState('');
  // Multi-category
  const [usedCategories, setUsedCategories] = useState([]);
  const [showAddCategory, setShowAddCategory] = useState(false);
  const [allProducts, setAllProducts] = useState([]);
  const [productsDiscussed, setProductsDiscussed] = useState([]);
  const [othersProductText, setOthersProductText] = useState('');
  const [productsHigherInterest, setProductsHigherInterest] = useState([]);
  const [brochuresPricesShared, setBrochuresPricesShared] = useState('');
  const [samplesShared, setSamplesShared] = useState('');
  const [doctorHasSamples, setDoctorHasSamples] = useState('');
  const [doctorSampleProducts, setDoctorSampleProducts] = useState([]);
  const [callExperience, setCallExperience] = useState('');
  const [followUpActions, setFollowUpActions] = useState([]);
  const [followUpOthersText, setFollowUpOthersText] = useState('');
  const [followUpOption, setFollowUpOption] = useState('');
  const [customDate, setCustomDate] = useState('');
  const [personalNotes, setPersonalNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showNewContactModal, setShowNewContactModal] = useState(false);

  const [newContactData, setNewContactData] = useState({ name: '', designation: '', whatsapp_number: '', email: '', can_influence_decisions: '' });
  const [addingContact, setAddingContact] = useState(false);

  useEffect(() => { fetchProducts(); }, []);

  useEffect(() => {
    const warn = e => { if (selectedAccount || callType) { e.preventDefault(); e.returnValue = ''; } };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [selectedAccount, callType]);

  useEffect(() => {
    const handler = e => { if (searchRef.current && !searchRef.current.contains(e.target)) setShowSearchResults(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (accountSearch.length < 2) { setSearchResults([]); return; }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const token = localStorage.getItem('token');
        const r = await axios.get(`${API}/accounts/search?q=${encodeURIComponent(accountSearch)}`, { headers: { Authorization: `Bearer ${token}` } });
        setSearchResults(r.data);
        setShowSearchResults(true);
      } catch {} finally { setSearching(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [accountSearch]);

  const fetchProducts = async () => {
    try {
      const token = localStorage.getItem('token');
      const r = await axios.get(`${API}/products`, { headers: { Authorization: `Bearer ${token}` } });
      setAllProducts(r.data);
    } catch {}
  };

  const handleSelectAccount = async account => {
    // Always fetch full fresh account from DB — search results may be stale
    setAccountSearch(account.name);
    setShowSearchResults(false);
    setContactPersonId('');
    setProductsDiscussed([]);
    setProductsHigherInterest([]);
    try {
      const token = localStorage.getItem('token');
      const [fullAcct, intelR, callsR] = await Promise.all([
        axios.get(`${API}/accounts/${account.id}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/accounts/${account.id}/product-intelligence`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/accounts/${account.id}/sales-history`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      const freshAccount = fullAcct.data;
      setSelectedAccount(freshAccount);
      const isFirstCall = !callsRes.data?.length;
      setMissingFields(getMissingFields(freshAccount, isFirstCall));
      setExistingIntel(intelR.data || []);
      const calls = callsR.data?.sales_calls || [];
      const hasComplaint = calls.some(c =>
        c.call_category === 'Complaint Addressal Call' ||
        c.telephonic_purpose === 'Complaint Addressal Call'
      );
      setHasExistingComplaint(hasComplaint);
    } catch {
      // Fallback to search result if fetch fails
      setSelectedAccount(account);
      setMissingFields(getMissingFields(account));
    }
  };

  const filteredContacts = () => {
    if (!selectedAccount) return [];
    const isDM = callType === 'In-Person Visit' ? IS_DM[whoMet] : undefined;
    if (isDM === undefined) return selectedAccount.contacts || [];
    return (selectedAccount.contacts || []).filter(c => isDM ? c.can_influence_decisions === 'Yes' : c.can_influence_decisions !== 'Yes');
  };

  const selectedContact = selectedAccount?.contacts?.find(c => c.id === contactPersonId);

  const handleAddNewContact = async () => {
    if (!newContactData.name || !newContactData.designation || !newContactData.whatsapp_number || !newContactData.can_influence_decisions) {
      toast.error('All fields required'); return;
    }
    setAddingContact(true);
    try {
      const token = localStorage.getItem('token');
      const r = await axios.post(`${API}/accounts/${selectedAccount.id}/contacts`,
        { ...newContactData, email: newContactData.email || null },
        { headers: { Authorization: `Bearer ${token}` } });
      const acctR = await axios.get(`${API}/accounts/${selectedAccount.id}`, { headers: { Authorization: `Bearer ${token}` } });
      setSelectedAccount(acctR.data);
      setMissingFields(getMissingFields(acctR.data));
      setContactPersonId(r.data.contact.id);
      setShowNewContactModal(false);
      setNewContactData({ name: '', designation: '', whatsapp_number: '', email: '', can_influence_decisions: '' });
      toast.success('Contact added!');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setAddingContact(false); }
  };


    const getFollowUpDate = () => {
    if (followUpOption === 'Custom Date') return customDate || null;
    const d = new Date();
    if (followUpOption === 'In 7 Days') { d.setDate(d.getDate() + 7); return d.toISOString().slice(0, 10); }
    if (followUpOption === 'In 14 Days') { d.setDate(d.getDate() + 14); return d.toISOString().slice(0, 10); }
    if (followUpOption === 'Representative to take over the account') return 'representative-takeover';
    return null;
  };

  const effectiveCategory = callType === 'Telephonic Call' ? telephonicPurpose : callCategory;
  const needsProducts = ['Product Promotion Call', // not Complaint (uses complaintProducts) not Case Follow-Up (handled separately)
    'Cold call to promote a product', 'Call based on a doctor referral to promote a product',
    'Follow up call post a Webinar / Education session'].includes(effectiveCategory);
  const isWorkshop = effectiveCategory === 'Workshop Invite Call';
  const isPayment = effectiveCategory === 'Payment Follow-Up Call';
  const isOthers = effectiveCategory === 'Others';
  const isComplaint = effectiveCategory === 'Complaint Addressal Call';
  const isCaseFollowUp = effectiveCategory === 'Case Follow-Up Call';

  const validate = () => {
    if (!selectedAccount) { toast.error('Please select a clinic'); return false; }
    if (missingFields.length > 0) { toast.error('Please complete account details first'); return false; }
    if (callType === 'In-Person Visit' && !contactPersonId) { toast.error('Please select a contact person'); return false; }
    if (callType === 'In-Person Visit' && !callCategory) { toast.error('Please select call category'); return false; }
    if (callType === 'Telephonic Call' && !contactPersonId) { toast.error('Please select who was called'); return false; }
    if (callType === 'Telephonic Call' && !telephonicPurpose) { toast.error('Please select call purpose'); return false; }
    if (telephonicPurpose === 'Others' && !telephonicOthers.trim()) { toast.error('Please describe purpose'); return false; }
    if (callCategory === 'Others' && !othersNotes.trim()) { toast.error('Please enter call notes'); return false; }
    if (isWorkshop && (!workshopDetails.trim() || !workshopOutcome)) { toast.error('Please complete workshop details'); return false; }
    if (isPayment && !paymentOutcome) { toast.error('Please select payment outcome'); return false; }
    if (needsProducts && !isComplaint && productsDiscussed.length === 0) { toast.error('Please select products discussed'); return false; }
    if (isComplaint && complaintProducts.length === 0) { toast.error('Please select products for complaint'); return false; }
    if (isComplaint && !complaintClosed) { toast.error('Please select if complaint is closed'); return false; }
    // Call experience only required for Product Promotion calls
    if (needsProducts && !isComplaint && !callExperience) { toast.error('Please select overall call experience'); return false; }
    if (!followUpOption) { toast.error('Please select follow-up timeline'); return false; }
    if (followUpOption === 'Custom Date' && !customDate) { toast.error('Please enter custom date'); return false; }
    return true;
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/sales-calls/v2`, {
        call_date: callDate,
        account_id: selectedAccount.id,
        call_type: callType,
        call_duration: '',
        who_met: 'In-Person Visit',
        contact_person_id: contactPersonId,
        call_category: callType === 'Telephonic Call' ? telephonicPurpose : callCategory,
        telephonic_purpose: telephonicPurpose,
        telephonic_others: telephonicOthers,
        others_notes: othersNotes,
        workshop_details: workshopDetails,
        workshop_outcome: workshopOutcome,
        payment_outcome: paymentOutcome,
        products_discussed: productsDiscussed,
        products_discussed_others: othersProductText,
        complaint_products: complaintProducts,
        complaint_products_others: complaintProductsOthers,
        complaint_closed: complaintClosed,
        complaint_follow_ups: complaintFollowUps,
        complaint_follow_ups_others: complaintFollowUpsOthers,
        products_higher_interest: productsHigherInterest,
        brochures_prices_shared: brochuresPricesShared,
        samples_shared: samplesShared,
        call_experience: callExperience,
        follow_up_actions: followUpActions,
        follow_up_others_text: followUpOthersText,
        personal_notes: personalNotes,
        follow_up_date: getFollowUpDate(),
        products: [],
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Sales call registered! 🎉');
      const newUsed = [...usedCategories, effectiveCategory];
      setUsedCategories(newUsed);
      resetCategoryFields();
      setShowAddCategory(true);
    } catch (err) {
      const detail = err.response?.data?.detail || err.response?.data?.message || JSON.stringify(err.response?.data) || err.message || 'Failed to register';
      console.error('Call save error:', err.response?.data);
      toast.error(detail, { duration: 8000 });
    } finally { setSubmitting(false); }
  };

  const autoLoadAccount = async (accountId) => {
    try {
      const token = localStorage.getItem('token');
      const [acctRes, callsRes] = await Promise.all([
        axios.get(`${API}/accounts/${accountId}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/accounts/${accountId}/sales-history`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] }))
      ]);
      const account = acctRes.data;
      const isFirstCall = !callsRes.data?.length;
      setSelectedAccount(account);
      setAccountSearch(account.name);
      setMissingFields(getMissingFields(account, isFirstCall));
      // Pre-fill inline city if missing
      if (!account.city) setInlineCity('');
      if (!account.clinic_potential) setInlinePotential('');
    } catch { toast.error('Failed to load account'); }
  };

  const checkDuplicateAccounts = async (name) => {
    if (!name || name.length < 3) { setDuplicateSuggestions([]); return; }
    setCheckingDuplicateAccount(true);
    try {
      const token = localStorage.getItem('token');
      const r = await axios.get(`${API}/accounts/search?q=${encodeURIComponent(name)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDuplicateSuggestions(r.data.slice(0, 3));
    } catch {}
    finally { setCheckingDuplicateAccount(false); }
  };

  const handleCreateNewAccount = async () => {
    if (!newAccountData.name) { toast.error('Clinic name is required'); return; }
    if (!newAccountData.address) { toast.error('Address is required'); return; }
    if (!newAccountData.city) { toast.error('City is required'); return; }
    setSavingNewAccount(true);
    try {
      const token = localStorage.getItem('token');
      const payload = {
        name: newAccountData.name,
        address: newAccountData.address,
        city: newAccountData.city,
        pincode: newAccountData.pincode || '',
        clinic_potential: newAccountData.clinic_potential || '',
        contacts: [],
        current_sales_value: 0,
      };
      const r = await axios.post(`${API}/accounts`, payload, { headers: { Authorization: `Bearer ${token}` } });
      setSelectedAccount(r.data);
      setAccountSearch(r.data.name);
      setShowNewAccountForm(false);
      setNewAccountData({ name: '', address: '', city: '', pincode: '', clinic_potential: '' });
      setMissingFields([]);
      toast.success('Account created! ✅');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create account'); }
    finally { setSavingNewAccount(false); }
  };

  const handleSaveInlineMissing = async () => {
    if (missingFields.includes('Clinic Potential') && !inlinePotential) { toast.error('Please select Clinic Potential'); return; }
    if (missingFields.includes('City') && !inlineCity.trim()) { toast.error('Please enter city'); return; }
    try {
      const token = localStorage.getItem('token');
      const updates = {};
      if (inlinePotential) updates.clinic_potential = inlinePotential;
      if (inlineCity.trim()) updates.city = inlineCity.trim();
      await axios.put(`${API}/accounts/${selectedAccount.id}/fill-missing-fields`, updates, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const fresh = await axios.get(`${API}/accounts/${selectedAccount.id}`, { headers: { Authorization: `Bearer ${token}` } });
      setSelectedAccount(fresh.data);
      setMissingFields(getMissingFields(fresh.data));
      setInlinePotential('');
      setInlineCity('');
      toast.success('Account details saved! ✅');
    } catch { toast.error('Failed to save account details'); }
  };

  const resetCategoryFields = () => {
    setCallCategory('');
    setTelephonicPurpose('');
    setTelephonicOthers('');
    setOthersNotes('');
    setWorkshopDetails('');
    setWorkshopOutcome('');
    setPaymentOutcome('');
    setProductsDiscussed([]);
    setOthersProductText('');
    setProductsHigherInterest([]);
    setBrochuresPricesShared('');
    setSamplesShared('');
    setCallExperience('');
    setFollowUpActions([]);
    setFollowUpOthersText('');
    setComplaintProducts([]);
    setComplaintProductsOthers('');
    setComplaintClosed('');
    setComplaintFollowUps([]);
    setComplaintFollowUpsOthers('');
    setFollowUpOption('');
    setCustomDate('');
    setPersonalNotes('');
  };

  const safeNavigate = path => {
    if (selectedAccount || callType) {
      if (window.confirm('You have unsaved changes. Leave anyway?')) navigate(path);
    } else navigate(path);
  };

  let step = 0;
  const s = () => ++step;

  return (
    <Layout onLogout={onLogout}>
      <div className="p-4 max-w-2xl mx-auto pb-20">
        {/* Header */}
        <div className="flex items-center gap-3 mb-5">
          <button onClick={() => safeNavigate('/dashboard')} className="p-2 hover:bg-slate-100 rounded-lg">
            <ArrowLeft className="h-5 w-5 text-slate-500" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>Log a Sales Call</h1>
            <p className="text-xs text-slate-500 mt-0.5">Fill in the details of your visit</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">

          {/* S1: Date */}
          <Section step={s()} title="Call Date">
            <Input type="date" value={callDate} onChange={e => setCallDate(e.target.value)}
              className="h-12 rounded-xl text-base" />
          </Section>

          {/* S2: Select Clinic / Account */}
          {(
            <Section step={s()} title="Select Clinic / Account">
              <div className="relative" ref={searchRef}>
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                <Input value={accountSearch}
                  onChange={e => { setAccountSearch(e.target.value); setSelectedAccount(null); setMissingFields([]); }}
                  placeholder="Search clinic..." className="pl-10 h-12 rounded-xl text-base" />
                {searching && <div className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin h-4 w-4 border-2 border-teal-500 border-t-transparent rounded-full" />}
                {showSearchResults && (
                  <div className="absolute top-14 left-0 right-0 bg-white rounded-xl border border-slate-200 shadow-xl z-50 max-h-64 overflow-y-auto">
                    {searchResults.length === 0 && accountSearch.length >= 2 && (
                      <div className="px-4 py-3 text-sm text-slate-400 border-b border-slate-100">
                        No accounts found for "{accountSearch}"
                      </div>
                    )}
                    {searchResults.map(acc => (
                      <button key={acc.id} type="button" onClick={() => handleSelectAccount(acc)}
                        className="w-full text-left px-4 py-3 hover:bg-teal-50 border-b last:border-0">
                        <p className="font-semibold text-slate-800 text-sm">{acc.name}</p>
                        <p className="text-xs text-slate-500 mt-0.5">📍 {acc.address}{acc.city ? `, ${acc.city}` : ''}</p>
                      </button>
                    ))}
                    <button type="button" onClick={() => { setShowNewAccountForm(true); setShowSearchResults(false); }}
                      className="w-full text-left px-4 py-3 hover:bg-emerald-50 text-emerald-700 font-semibold text-sm flex items-center gap-2 border-t border-slate-100">
                      <Plus className="h-4 w-4" /> Add New Account
                    </button>
                  </div>
                )}
              </div>

              {selectedAccount && missingFields.length > 0 && (
                <div className="mt-3 p-4 bg-amber-50 rounded-xl border border-amber-300">
                  <div className="flex items-start gap-2 mb-3">
                    <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold text-amber-800 text-sm">Account incomplete — please fill before logging a call</p>
                      <ul className="mt-1 space-y-0.5">
                        {missingFields.map(f => <li key={f} className="text-xs text-amber-700">• {f}</li>)}
                      </ul>
                    </div>
                  </div>
                  {/* Inline fill missing fields */}
                  <div className="space-y-3 mt-3 p-3 bg-white rounded-xl border border-amber-200">
                    <p className="text-xs font-semibold text-amber-700">Fill in below to continue:</p>
                    {missingFields.includes('Clinic Potential') && (
                      <div>
                        <p className="text-xs text-slate-600 mb-1 font-medium">Clinic Potential *</p>
                        <div className="flex gap-2">
                          {['High', 'Medium', 'Low'].map(opt => (
                            <button key={opt} type="button"
                              onClick={() => setInlinePotential(opt)}
                              className={`flex-1 py-2 rounded-xl border-2 text-xs font-bold transition-all ${
                                inlinePotential === opt
                                  ? opt === 'High' ? 'bg-emerald-600 text-white border-emerald-600'
                                  : opt === 'Medium' ? 'bg-amber-500 text-white border-amber-500'
                                  : 'bg-red-500 text-white border-red-500'
                                  : 'bg-white text-slate-600 border-slate-200'
                              }`}>{opt}</button>
                          ))}
                        </div>
                      </div>
                    )}
                    {missingFields.includes('City') && (
                      <div>
                        <p className="text-xs text-slate-600 mb-1 font-medium">City *</p>
                        <Input value={inlineCity} onChange={e => setInlineCity(e.target.value)}
                          placeholder="Enter city" className="h-9 rounded-lg text-sm" />
                      </div>
                    )}
                    <button type="button" onClick={handleSaveInlineMissing}
                      className="w-full py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-xl text-sm font-semibold">
                      Save & Continue →
                    </button>
                  </div>
                </div>
              )}

              {/* Inline new account form */}
              {showNewAccountForm && (
                <div className="mt-3 p-4 bg-emerald-50 rounded-xl border border-emerald-200 space-y-3">
                  <p className="text-sm font-bold text-emerald-800">New Account Details</p>
                  <div>
                    <Label className="text-xs text-slate-600 mb-1 block">Clinic Name *</Label>
                    <Input value={newAccountData.name}
                      onChange={e => { setNewAccountData(p => ({...p, name: e.target.value})); checkDuplicateAccounts(e.target.value); }}
                      placeholder="Clinic name" className="rounded-xl h-10" />
                    {checkingDuplicateAccount && <p className="text-xs text-slate-400 mt-1">Checking for duplicates...</p>}
                    {duplicateSuggestions.length > 0 && (
                      <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded-xl">
                        <p className="text-xs font-semibold text-amber-800 mb-1">⚠️ Similar accounts found — did you mean one of these?</p>
                        {duplicateSuggestions.map(acc => (
                          <button key={acc.id} type="button"
                            onClick={() => { handleSelectAccount(acc); setShowNewAccountForm(false); setDuplicateSuggestions([]); setNewAccountData({ name: '', address: '', city: '', pincode: '', clinic_potential: '' }); }}
                            className="w-full text-left px-2 py-1.5 hover:bg-amber-100 rounded-lg mt-1">
                            <p className="text-xs font-semibold text-amber-800">{acc.name}</p>
                            <p className="text-xs text-amber-600">📍 {acc.city} · {acc.address?.slice(0, 30)}</p>
                          </button>
                        ))}
                        <p className="text-xs text-slate-500 mt-1">Or continue below to create a new account</p>
                      </div>
                    )}
                  </div>
                  <div>
                    <Label className="text-xs text-slate-600 mb-1 block">Address *</Label>
                    <Input value={newAccountData.address} onChange={e => setNewAccountData(p => ({...p, address: e.target.value}))} placeholder="Full address" className="rounded-xl h-10" />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-600 mb-1 block">City *</Label>
                    <Input value={newAccountData.city} onChange={e => setNewAccountData(p => ({...p, city: e.target.value}))} placeholder="City" className="rounded-xl h-10" />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-600 mb-1 block">Pincode</Label>
                    <Input value={newAccountData.pincode} onChange={e => setNewAccountData(p => ({...p, pincode: e.target.value}))} placeholder="Pincode" className="rounded-xl h-10" />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-600 mb-2 block">Clinic Potential</Label>
                    <div className="flex gap-2">
                      {['High', 'Medium', 'Low'].map(opt => (
                        <button key={opt} type="button" onClick={() => setNewAccountData(p => ({...p, clinic_potential: opt}))}
                          className={`flex-1 py-2 rounded-xl border-2 text-xs font-bold transition-all ${
                            newAccountData.clinic_potential === opt
                              ? opt === 'High' ? 'bg-emerald-600 text-white border-emerald-600'
                              : opt === 'Medium' ? 'bg-amber-500 text-white border-amber-500'
                              : 'bg-red-500 text-white border-red-500'
                              : 'bg-white text-slate-600 border-slate-200'
                          }`}>{opt}</button>
                      ))}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button type="button" onClick={handleCreateNewAccount} disabled={savingNewAccount}
                      className="flex-1 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-sm font-semibold">
                      {savingNewAccount ? 'Creating...' : '✅ Create Account'}
                    </button>
                    <button type="button" onClick={() => setShowNewAccountForm(false)}
                      className="px-4 py-2.5 border border-slate-200 rounded-xl text-sm text-slate-600">
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {selectedAccount && missingFields.length === 0 && (
                <div className="mt-3 p-3 bg-teal-50 rounded-xl border border-teal-200 flex items-start justify-between">
                  <div>
                    <p className="font-bold text-teal-800 text-sm">{selectedAccount.name}</p>
                    <p className="text-xs text-teal-700 mt-0.5">📍 {selectedAccount.address}{selectedAccount.city ? `, ${selectedAccount.city}` : ''}</p>
                    {selectedAccount.clinic_potential && (
                      <span className={`inline-block mt-1 text-xs px-2 py-0.5 rounded-lg font-medium ${
                        selectedAccount.clinic_potential === 'High' ? 'bg-emerald-100 text-emerald-700' :
                        selectedAccount.clinic_potential === 'Medium' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'
                      }`}>Clinic Potential: {selectedAccount.clinic_potential}</span>
                    )}
                  </div>
                  <button type="button" onClick={() => { setSelectedAccount(null); setAccountSearch(''); setMissingFields([]); }}>
                    <X className="h-4 w-4 text-violet-400" />
                  </button>
                </div>
              )}
            </Section>
          )}

          {/* Only show further steps if account is complete */}
          {selectedAccount && missingFields.length === 0 && (<>

            {/* S3: Who did you meet? — all contacts, influence prompt if missing */}
            <Section step={s()} title="Who did you meet?" subtitle="Select contact or add a new one">
              <div className="space-y-2">
                {(selectedAccount?.contacts || []).length === 0 && (
                  <p className="text-sm text-slate-400 text-center py-3">No contacts yet — add one below</p>
                )}
                {(selectedAccount?.contacts || []).map(c => (
                  <div key={c.id}>
                    <button type="button" onClick={() => setContactPersonId(c.id)}
                      className={`w-full flex items-center gap-3 p-3 rounded-xl border-2 transition-all text-left ${contactPersonId === c.id ? 'border-teal-500 bg-teal-50' : 'border-slate-200 hover:border-teal-300'}`}>
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-teal-600 to-emerald-400 flex items-center justify-center flex-shrink-0">
                        <span className="text-white font-bold">{c.name?.charAt(0) || '?'}</span>
                      </div>
                      <div className="flex-1">
                        <p className="font-semibold text-slate-800 text-sm">Dr. {c.name}</p>
                        <p className="text-xs text-slate-400">{c.designation} · 📞 {c.whatsapp_number}</p>
                        {c.can_influence_decisions
                          ? <span className={`text-xs px-2 py-0.5 rounded-lg font-medium ${c.can_influence_decisions === 'High' ? 'bg-emerald-100 text-emerald-700' : c.can_influence_decisions === 'Medium' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>
                              Influence: {c.can_influence_decisions}
                            </span>
                          : <span className="text-xs text-amber-600 font-medium">⚠️ Purchase influence not set — tap to set</span>
                        }
                      </div>
                    </button>
                    {/* Inline influence update if not set */}
                    {contactPersonId === c.id && !c.can_influence_decisions && (
                      <div className="ml-3 mt-2 p-3 bg-amber-50 rounded-xl border border-amber-200">
                        <p className="text-xs font-semibold text-amber-800 mb-2">Set Influence on Purchase Decision:</p>
                        <div className="flex gap-2">
                          {['High', 'Medium', 'Low'].map(opt => (
                            <button key={opt} type="button" onClick={async () => {
                              try {
                                const token = localStorage.getItem('token');
                                // Update contact influence directly
                                const updatedContacts = selectedAccount.contacts.map(ct =>
                                  ct.id === c.id ? {...ct, can_influence_decisions: opt} : ct
                                );
                                await axios.put(`${API}/accounts/${selectedAccount.id}/fill-missing-fields`,
                                  { contacts: updatedContacts }, { headers: { Authorization: `Bearer ${token}` } });
                                const fresh = await axios.get(`${API}/accounts/${selectedAccount.id}`, { headers: { Authorization: `Bearer ${token}` } });
                                setSelectedAccount(fresh.data);
                                toast.success('Influence level saved ✅');
                              } catch { toast.error('Failed to save'); }
                            }}
                              className={`flex-1 py-2 rounded-xl border-2 text-xs font-bold transition-all ${
                                opt === 'High' ? 'border-emerald-400 hover:bg-emerald-600 hover:text-white' :
                                opt === 'Medium' ? 'border-amber-400 hover:bg-amber-500 hover:text-white' :
                                'border-red-400 hover:bg-red-500 hover:text-white'
                              } bg-white text-slate-600`}>
                              {opt}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
                <button type="button" onClick={() => setShowNewContactModal(true)}
                  className="flex items-center gap-2 text-sm text-teal-700 font-medium mt-1 p-2 hover:bg-teal-50 rounded-xl w-full">
                  <Plus className="h-4 w-4" /> Add New Contact
                </button>
              </div>
            </Section>

            {/* Call Category (in-person only) */}
            {contactPersonId && callType === 'In-Person Visit' && (
              <Section step={s()} title="Call Category">
                {usedCategories.length > 0 && (
                  <div className="mb-3 p-3 bg-teal-50 rounded-xl border border-teal-200">
                    <p className="text-xs text-teal-700 font-medium">✅ Already logged: {usedCategories.join(', ')}</p>
                  </div>
                )}
                <div className="flex flex-col gap-2">
                  {INPERSON_CATEGORIES.map(cat => {
                    const isUsed = usedCategories.includes(cat);
                    return (
                      <button key={cat} type="button" disabled={isUsed}
                        onClick={() => { if (!isUsed) { setCallCategory(cat); setProductsDiscussed([]); setOthersProductText(''); setProductsHigherInterest([]); setCallExperience(''); setFollowUpActions([]); setComplaintProducts([]); setComplaintClosed(''); setComplaintFollowUps([]); } }}
                        className={`w-full px-4 py-3 rounded-xl border-2 text-sm font-semibold transition-all ${
                          isUsed ? 'border-slate-100 bg-slate-50 text-slate-300 cursor-not-allowed' :
                          callCategory === cat ? 'bg-teal-700 text-white border-violet-600 shadow-md' :
                          'bg-white text-slate-600 border-slate-200 hover:border-violet-400'
                        }`}>
                        {cat} {isUsed ? '✓' : ''}
                      </button>
                    );
                  })}
                </div>
              </Section>
            )}

                        {/* COMPLAINT FLOW — Seg 7: Which products */}
            {contactPersonId && isComplaint && (
              <Section step={s()} title="Complaints on which products?">
                <div className="flex flex-wrap gap-2">
                  {allProducts.map(p => (
                    <button key={p.id} type="button"
                      onClick={() => setComplaintProducts(prev =>
                        prev.includes(p.id) ? prev.filter(x => x !== p.id) : [...prev, p.id])}
                      className={`px-4 py-2.5 rounded-xl border-2 text-sm font-semibold transition-all ${
                        complaintProducts.includes(p.id) ? 'bg-red-500 text-white border-red-500' : 'bg-white text-slate-600 border-slate-200 hover:border-red-400'
                      }`}>
                      {p.name}
                    </button>
                  ))}
                  <button type="button"
                    onClick={() => {
                      const newList = complaintProducts.includes('others')
                        ? complaintProducts.filter(x => x !== 'others')
                        : [...complaintProducts, 'others'];
                      setComplaintProducts(newList);
                      if (!newList.includes('others')) setComplaintProductsOthers('');
                    }}
                    className={`px-4 py-2.5 rounded-xl border-2 text-sm font-semibold transition-all ${
                      complaintProducts.includes('others') ? 'bg-red-500 text-white border-red-500' : 'bg-white text-slate-600 border-slate-200 hover:border-red-400'
                    }`}>
                    Others
                  </button>
                </div>
                {complaintProducts.includes('others') && (
                  <input value={complaintProductsOthers} onChange={e => setComplaintProductsOthers(e.target.value)}
                    placeholder="Enter product name..."
                    className="mt-3 w-full h-11 rounded-xl border border-slate-200 focus:border-red-400 focus:outline-none px-3 text-sm" />
                )}
              </Section>
            )}

            {/* COMPLAINT FLOW — Seg 8: Complaint Closed? */}
            {contactPersonId && isComplaint && complaintProducts.length > 0 && (
              <Section step={s()} title="Complaint Closed?">
                <div className="grid grid-cols-2 gap-3">
                  {['Yes', 'No'].map(opt => (
                    <button key={opt} type="button" onClick={() => setComplaintClosed(opt)}
                      className={`py-3 rounded-xl border-2 text-sm font-bold transition-all ${
                        complaintClosed === opt
                          ? opt === 'Yes' ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-red-500 text-white border-red-500'
                          : 'bg-white text-slate-600 border-slate-200 hover:border-violet-400'
                      }`}>
                      {opt === 'Yes' ? '✅ Yes — Closed' : '❌ No — Open'}
                    </button>
                  ))}
                </div>
              </Section>
            )}

            {/* COMPLAINT FLOW — Seg 9: Follow-Up Actions */}
            {contactPersonId && isComplaint && complaintClosed && (
              <Section step={s()} title="Follow-Up Actions">
                <div className="space-y-2">
                  {COMPLAINT_FOLLOWUP_ACTIONS.map(action => (
                    <button key={action} type="button"
                      onClick={() => setComplaintFollowUps(prev =>
                        prev.includes(action) ? prev.filter(x => x !== action) : [...prev, action])}
                      className="flex items-center gap-3 w-full text-left px-3 py-3 rounded-xl border border-slate-200 hover:border-teal-300 active:bg-teal-50 transition-all">
                      {complaintFollowUps.includes(action)
                        ? <CheckSquare className="h-5 w-5 text-teal-700 flex-shrink-0" />
                        : <Square className="h-5 w-5 text-slate-300 flex-shrink-0" />}
                      <span className="text-sm text-slate-700">{action}</span>
                    </button>
                  ))}
                  <button type="button"
                    onClick={() => setComplaintFollowUps(prev =>
                      prev.includes('Others') ? prev.filter(x => x !== 'Others') : [...prev, 'Others'])}
                    className="flex items-center gap-3 w-full text-left px-3 py-3 rounded-xl border border-slate-200 hover:border-teal-300 transition-all">
                    {complaintFollowUps.includes('Others')
                      ? <CheckSquare className="h-5 w-5 text-teal-700 flex-shrink-0" />
                      : <Square className="h-5 w-5 text-slate-300 flex-shrink-0" />}
                    <span className="text-sm text-slate-700">Others</span>
                  </button>
                  {complaintFollowUps.includes('Others') && (
                    <input value={complaintFollowUpsOthers} onChange={e => setComplaintFollowUpsOthers(e.target.value)}
                      placeholder="Please specify..."
                      className="w-full h-11 rounded-xl border border-slate-200 focus:border-teal-500 focus:outline-none px-3 text-sm" />
                  )}
                </div>
              </Section>
            )}

            {/* CASE FOLLOW-UP FLOW — Products Discussed */}
            {contactPersonId && isCaseFollowUp && (
              <Section step={s()} title="Case Follow-Up — Products Discussed">
                <div className="flex flex-wrap gap-2">
                  {allProducts.map(p => (
                    <button key={p.id} type="button"
                      onClick={() => setProductsDiscussed(prev => prev.includes(p.id) ? prev.filter(x => x !== p.id) : [...prev, p.id])}
                      className={`px-4 py-2.5 rounded-xl border-2 text-sm font-semibold transition-all ${
                        productsDiscussed.includes(p.id) ? 'bg-teal-700 text-white border-teal-700' : 'bg-white text-slate-600 border-slate-200 hover:border-teal-400'
                      }`}>
                      {p.name}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-slate-400 mt-2">Select products relevant to this case</p>
              </Section>
            )}

            {/* PRODUCT PROMOTION FLOW — Seg 7: Products Discussed */}
            {contactPersonId && effectiveCategory && needsProducts && !isComplaint && !isCaseFollowUp && (
              <Section step={s()} title="Products Discussed" subtitle="Select all products discussed in this call">
                <div className="flex flex-wrap gap-2">
                  {allProducts.map(p => (
                    <button key={p.id} type="button"
                      onClick={() => {
                        const newList = productsDiscussed.includes(p.id)
                          ? productsDiscussed.filter(x => x !== p.id)
                          : [...productsDiscussed, p.id];
                        setProductsDiscussed(newList);
                      }}
                      className={`px-4 py-2.5 rounded-xl border-2 text-sm font-semibold transition-all ${
                        productsDiscussed.includes(p.id) ? 'bg-teal-700 text-white border-violet-600' : 'bg-white text-slate-600 border-slate-200 hover:border-violet-400'
                      }`}>
                      {p.name}
                    </button>
                  ))}
                  <button type="button"
                    onClick={() => {
                      const newList = productsDiscussed.includes('others')
                        ? productsDiscussed.filter(x => x !== 'others')
                        : [...productsDiscussed, 'others'];
                      setProductsDiscussed(newList);
                      if (!newList.includes('others')) {
                        setOthersProductText('');
                        setProductsHigherInterest(prev => prev.filter(x => x !== 'others'));
                      }
                    }}
                    className={`px-4 py-2.5 rounded-xl border-2 text-sm font-semibold transition-all ${
                      productsDiscussed.includes('others') ? 'bg-teal-700 text-white border-violet-600' : 'bg-white text-slate-600 border-slate-200 hover:border-violet-400'
                    }`}>
                    Others
                  </button>
                </div>
                {productsDiscussed.includes('others') && (
                  <Input
                    value={othersProductText}
                    onChange={e => setOthersProductText(e.target.value)}
                    placeholder="Enter product name..."
                    className="mt-3 rounded-xl"
                  />
                )}
              </Section>
            )}

                        {/* Products with Higher Interest */}
            {contactPersonId && effectiveCategory && needsProducts && productsDiscussed.length > 0 && (
              <Section step={s()} title="Products with Higher Interest Expressed"
                subtitle="Select any product the doctor showed higher interest in">
                <div className="flex flex-wrap gap-2">
                  {allProducts.map(p => (
                    <button key={p.id} type="button"
                      onClick={() => setProductsHigherInterest(prev =>
                        prev.includes(p.id) ? prev.filter(x => x !== p.id) : [...prev, p.id])}
                      className={`px-4 py-2.5 rounded-xl border-2 text-sm font-semibold transition-all ${
                        productsHigherInterest.includes(p.id) ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-slate-600 border-slate-200 hover:border-emerald-400'
                      }`}>
                      {p.name}
                    </button>
                  ))}
                  {othersProductText.trim() && (
                    <button type="button"
                      onClick={() => setProductsHigherInterest(prev =>
                        prev.includes('others') ? prev.filter(x => x !== 'others') : [...prev, 'others'])}
                      className={`px-4 py-2.5 rounded-xl border-2 text-sm font-semibold transition-all ${
                        productsHigherInterest.includes('others') ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-slate-600 border-slate-200 hover:border-emerald-400'
                      }`}>
                      {othersProductText}
                    </button>
                  )}
                </div>
                <p className="text-xs text-slate-400 mt-2">Leave empty if no product had higher interest</p>
              </Section>
            )}

            {/* Segment 7: Doctor has samples? */}
            {contactPersonId && effectiveCategory && needsProducts && productsDiscussed.length > 0 && (
              <Section step={s()} title="Does the doctor have Samples of our products?">
                <div className="flex gap-3">
                  {['Yes', 'No'].map(opt => (
                    <button key={opt} type="button" onClick={() => { setDoctorHasSamples(opt); if (opt === 'No') setDoctorSampleProducts([]); }}
                      className={`flex-1 py-3 rounded-xl border-2 text-sm font-semibold transition-all ${
                        doctorHasSamples === opt
                          ? opt === 'Yes' ? 'bg-teal-700 text-white border-teal-700' : 'bg-slate-600 text-white border-slate-600'
                          : 'bg-white text-slate-600 border-slate-200 hover:border-teal-400'
                      }`}>{opt}</button>
                  ))}
                </div>
                {doctorHasSamples === 'Yes' && (
                  <div className="mt-4 space-y-2">
                    <p className="text-sm font-semibold text-slate-700">Which product samples of ours does the doctor have?</p>
                    <div className="flex flex-wrap gap-2">
                      {allProducts.map(p => (
                        <button key={p.id} type="button"
                          onClick={() => setDoctorSampleProducts(prev =>
                            prev.includes(p.id) ? prev.filter(x => x !== p.id) : [...prev, p.id]
                          )}
                          className={`px-4 py-2 rounded-xl border-2 text-sm font-semibold transition-all ${
                            doctorSampleProducts.includes(p.id)
                              ? 'bg-teal-700 text-white border-teal-700'
                              : 'bg-white text-slate-600 border-slate-200 hover:border-teal-400'
                          }`}>{p.name}</button>
                      ))}
                    </div>
                    <p className="text-xs text-slate-400">Select all that apply</p>
                  </div>
                )}
              </Section>
            )}

            {/* Segment 8: Brochures and Pricing Given */}
            {contactPersonId && effectiveCategory && needsProducts && productsDiscussed.length > 0 && (
              <Section step={s()} title="Has a message been sent to the doctor about our latest product pricing?">
                <div className="flex gap-3">
                  {['Yes', 'No'].map(opt => (
                    <button key={opt} type="button" onClick={() => setBrochuresPricesShared(opt)}
                      className={`flex-1 py-3 rounded-xl border-2 text-sm font-semibold transition-all ${
                        brochuresPricesShared === opt
                          ? opt === 'Yes' ? 'bg-teal-700 text-white border-teal-700' : 'bg-slate-600 text-white border-slate-600'
                          : 'bg-white text-slate-600 border-slate-200 hover:border-teal-400'
                      }`}>{opt}</button>
                  ))}
                </div>
              </Section>
            )}

            {/* Overall Call Experience */}
            {contactPersonId && effectiveCategory && needsProducts && !isComplaint && productsDiscussed.length > 0 && (
              <Section step={s()} title="Overall Call Experience *">
                <div className="flex flex-col gap-3">
                  <Chip label="🟢 Positive — Got / Can get orders" selected={callExperience === 'Positive'} fullWidth
                    onClick={() => setCallExperience('Positive')} color="emerald" />
                  <Chip label="🟡 Neutral — Keep Following Up" selected={callExperience === 'Neutral'} fullWidth
                    onClick={() => setCallExperience('Neutral')} color="amber" />
                  <Chip label="🔴 Negative — Doctor will not give orders" selected={callExperience === 'Negative'} fullWidth
                    onClick={() => setCallExperience('Negative')} color="red" />
                </div>
              </Section>
            )}

            {/* Workshop */}
            {contactPersonId && isWorkshop && (
              <Section step={s()} title="Workshop Details">
                <div className="space-y-4">
                  <div>
                    <Label className="text-sm font-semibold mb-2 block">Workshop Name & Date of Webinar/Workshop *</Label>
                    <Input value={workshopDetails} onChange={e => setWorkshopDetails(e.target.value)}
                      placeholder="e.g. Orthodontic Excellence Workshop — April 15, 2026" className="rounded-xl" />
                  </div>
                  <div>
                    <Label className="text-sm font-semibold mb-2 block">Doctor's Response *</Label>
                    <div className="flex flex-col gap-2">
                      {WORKSHOP_OUTCOMES.map(opt => (
                        <Chip key={opt} label={opt} selected={workshopOutcome === opt} fullWidth onClick={() => setWorkshopOutcome(opt)}
                          color={opt === 'Interested' ? 'emerald' : opt === 'Not Interested' ? 'red' : 'amber'} />
                      ))}
                    </div>
                  </div>
                </div>
              </Section>
            )}

            {/* Payment */}
            {contactPersonId && isPayment && (
              <Section step={s()} title="Payment Follow-Up Outcome">
                <div className="flex flex-col gap-2">
                  {PAYMENT_OUTCOMES.map(opt => (
                    <Chip key={opt} label={opt} selected={paymentOutcome === opt} fullWidth onClick={() => setPaymentOutcome(opt)}
                      color={opt === 'Positive' ? 'emerald' : opt === 'Negative' ? 'red' : 'amber'} />
                  ))}
                </div>
              </Section>
            )}

            {/* Others */}
            {contactPersonId && isOthers && (
              <Section step={s()} title="Call Notes">
                <textarea value={othersNotes} onChange={e => setOthersNotes(e.target.value)}
                  placeholder="Describe the purpose and outcome..." rows={4}
                  className="w-full rounded-xl border border-slate-200 focus:border-teal-500 focus:outline-none px-4 py-3 text-sm resize-none" />
              </Section>
            )}

            {/* Follow-Up Actions — not for Workshop/Payment/Complaint */}
            {contactPersonId && effectiveCategory && !isWorkshop && !isPayment && !isComplaint && (
              <Section step={s()} title="Follow-Up Actions">
                <Checklist
                  options={callType === 'Telephonic Call'
                    ? [...FOLLOW_UP_ACTIONS, TELEPHONIC_EXTRA_ACTION]
                    : FOLLOW_UP_ACTIONS}
                  selected={followUpActions}
                  onChange={setFollowUpActions}
                  othersText={followUpOthersText}
                  onOthersChange={setFollowUpOthersText}
                />
              </Section>
            )}

            {/* Follow-Up Date */}
            {contactPersonId && effectiveCategory && (
              <Section step={s()} title="Follow-Up Date *">
                <div className="flex flex-col gap-2 mb-3">
                  {(callType === 'Telephonic Call' ? TELEPHONIC_FOLLOW_UP_OPTIONS : FOLLOW_UP_OPTIONS).map(opt => (
                    <Chip key={opt} label={opt} selected={followUpOption === opt} fullWidth onClick={() => setFollowUpOption(opt)}
                      color={opt === 'Representative to take over the account' ? 'amber' : 'violet'} />
                  ))}
                </div>
                {followUpOption === 'Custom Date' && (
                  <Input type="date" value={customDate} onChange={e => setCustomDate(e.target.value)}
                    min={new Date().toISOString().slice(0, 10)} className="h-12 rounded-xl" />
                )}
              </Section>
            )}

            {/* Personal Notes */}
            {contactPersonId && effectiveCategory && (
              <Section step={s()} title="Personal Notes" subtitle="Optional — only visible to you and your manager">
                <textarea value={personalNotes} onChange={e => setPersonalNotes(e.target.value)}
                  placeholder={`Examples:\n• "Doctor mentioned expanding clinic in 3 months"\n• "Best time to visit: Tuesday mornings after 11am"`}
                  rows={4}
                  className="w-full rounded-xl border border-slate-200 focus:border-teal-500 focus:outline-none px-4 py-3 text-sm resize-none" />
              </Section>
            )}



            {/* Submit */}
            {followUpOption && (
              <div className="flex gap-3 pt-2 pb-4">
                <Button type="submit" disabled={submitting}
                  className="flex-1 h-14 bg-gradient-to-r from-teal-700 to-emerald-500 text-white font-bold text-base rounded-2xl shadow-lg active:scale-[0.98]">
                  <Save className="mr-2 h-5 w-5" />
                  {submitting ? 'Submitting...' : 'Submit Sales Call'}
                </Button>
                <Button type="button" variant="outline" onClick={() => safeNavigate('/dashboard')} className="h-14 rounded-2xl px-6">
                  Cancel
                </Button>
              </div>
            )}

          </>)}
        </form>


        {/* ── Success Banner — shown after call saved ─────────────────── */}
        {showAddCategory && (
          <div className="fixed inset-0 bg-black/40 z-40 flex items-end justify-center p-4">
            <div className="bg-white rounded-2xl w-full max-w-lg p-6 shadow-2xl space-y-4 mb-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-2xl">✅</span>
                </div>
                <div>
                  <p className="font-bold text-slate-800 text-lg">Call Category Logged!</p>
                  <p className="text-sm text-slate-500">{selectedAccount?.name}</p>
                </div>
              </div>
              {usedCategories.length > 0 && (
                <p className="text-xs text-teal-700 bg-teal-50 rounded-xl px-3 py-2">
                  ✓ Logged: {usedCategories.join(' · ')}
                </p>
              )}
              <p className="text-sm font-medium text-slate-700 text-center">
                Log another call category for the same account?
              </p>
              <div className="flex gap-3">
                <button type="button"
                  onClick={() => {
                    setShowAddCategory(false);
                    setCallCategory('');
                    resetCategoryFields();
                    window.scrollTo(0, 0);
                  }}
                  className="flex-1 py-3 bg-gradient-to-r from-teal-700 to-emerald-500 text-white rounded-xl text-sm font-bold">
                  ✅ Yes — Add Another
                </button>
                <button type="button"
                  onClick={() => {
                    // Full reset — start fresh
                    setShowAddCategory(false);
                    setSelectedAccount(null);
                    setAccountSearch('');
                    setMissingFields([]);
                    setContactPersonId('');
                    setUsedCategories([]);
                    resetCategoryFields();
                    setCallDate(new Date().toISOString().slice(0, 10));
                    window.scrollTo(0, 0);
                  }}
                  className="flex-1 py-3 border-2 border-slate-200 text-slate-600 font-semibold rounded-xl text-sm hover:bg-slate-50">
                  ❌ No — New Call
                </button>
              </div>
            </div>
          </div>
        )}


        {/* New Contact Modal */}
        <Dialog open={showNewContactModal} onOpenChange={setShowNewContactModal}>
          <DialogContent className="max-w-md mx-4">
            <DialogHeader><DialogTitle>Add New Contact</DialogTitle></DialogHeader>
            <div className="space-y-3 py-4">
              <div><Label className="text-xs">Full Name *</Label>
                <Input value={newContactData.name} onChange={e => setNewContactData(p => ({ ...p, name: e.target.value }))} className="mt-1" /></div>
              <div><Label className="text-xs">Designation *</Label>
                <select value={newContactData.designation} onChange={e => setNewContactData(p => ({ ...p, designation: e.target.value }))}
                  className="mt-1 w-full h-10 rounded-xl border border-slate-200 px-3 text-sm focus:outline-none focus:border-teal-500">
                  <option value="">Select...</option>
                  {DESIGNATION_OPTIONS.map(d => <option key={d} value={d}>{d}</option>)}
                </select></div>
              <div><Label className="text-xs">Phone Number *</Label>
                <Input value={newContactData.whatsapp_number} onChange={e => setNewContactData(p => ({ ...p, whatsapp_number: e.target.value }))} className="mt-1" /></div>
              <div><Label className="text-xs">Email (Optional)</Label>
                <Input value={newContactData.email} onChange={e => setNewContactData(p => ({ ...p, email: e.target.value }))} className="mt-1" type="email" /></div>
              <div>
                <Label className="text-xs">Influence on Purchase Decision? *</Label>
                <div className="flex gap-2 mt-1">
                  {['High', 'Medium', 'Low'].map(opt => (
                    <button key={opt} type="button" onClick={() => setNewContactData(p => ({ ...p, can_influence_decisions: opt }))}
                      className={`flex-1 py-2.5 rounded-xl border-2 text-sm font-semibold transition-all ${
                        newContactData.can_influence_decisions === opt
                          ? opt === 'High' ? 'bg-emerald-600 text-white border-emerald-600'
                          : opt === 'Medium' ? 'bg-amber-500 text-white border-amber-500'
                          : 'bg-red-500 text-white border-red-500'
                          : 'bg-white text-slate-600 border-slate-200'}`}>
                      {opt}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex gap-3 pt-2">
                <Button variant="outline" onClick={() => setShowNewContactModal(false)} className="flex-1">Cancel</Button>
                <Button onClick={handleAddNewContact} disabled={addingContact}
                  className="flex-1 bg-gradient-to-r from-teal-700 to-emerald-500 text-white rounded-xl">
                  {addingContact ? 'Adding...' : 'Add Contact'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default CreateSalesCall;
